/* eslint-disable no-underscore-dangle */
/* eslint-disable @typescript-eslint/no-this-alias */

'use-strict';

import { IScope } from 'angular';
import { SalesAPIService } from '../../shared/api/sales-api.service';
import { InterfaceService } from '../../shared/interface/interface.service';
import { TableService } from '../../shared/tables/table.service';
import { Bodycode, MissingBodycode } from '../../src/app/@shared-types/vehicle/bodycode';
import { EVehicleManufacturer } from '../../src/app/@shared-types/vehicle/enums';
import { FormToolsService } from '../../src/app/shared/services/form-tools.service';
import LogService from '../../src/app/shared/services/log';
import ObjectService from '../../src/app/shared/services/object';
import { ToastService, ToastType } from '../../ui-components/toast/toast.service';

export interface BodycodeScope extends IScope {
  editBodycode: Omit<Bodycode, '_id'> | null,
  overlay: {
    visibility: boolean,
    title: string,
  },
  bodycodeTable: any,
  bodycodes: Bodycode[] | null,
  missingBodycodes: MissingBodycode[] | null,
  addNewBodycode: boolean,
  exportDataset: () => void,
  submitForm: () => void,
  removeBodycode: (id: string) => void,
  openEditOverlay: (bodycode: Omit<Bodycode, '_id'> | null, newCode: boolean) => void,
  copyBodycode: (bodycode: Bodycode) => void,
  addMissingBodycode: (bodycode: string) => void,
  hideEditOverlay: () => void,
  removeAllFilter: () => void,
  removeFilter: (filter: string) => void,
}

/* @ngInject */
export default function BodycodeController(
  $scope: BodycodeScope,
  NgTableParams: any,
  tableService: TableService,
  toastService: ToastService,
  interfaceService: InterfaceService,
  salesApiService: SalesAPIService,
  logService: LogService,
  formToolsService: FormToolsService,
  objectService: ObjectService,
): void {
  $scope.editBodycode = null;
  $scope.overlay = {
    visibility: false,
    title: '',
  };

  $scope.bodycodes = null;
  $scope.missingBodycodes = [];

  $scope.addNewBodycode = false;

  const getEmptyBodycode = (code?: string): Omit<Bodycode, '_id'> => ({
    bodycode: code || '',
    comfort: {},
    environment: {},
    features: {},
    generalDetails: {},
    infotainment: {},
    make: '',
    manufacturer: EVehicleManufacturer.OTHER,
    model: '',
    modelDescription: '',
    modelyear: null,
    type: {},
    engine: {
      fuel: {},
      transmission: {},
    },
    assistance: {
      parking: {},
    },
    packets: {},
    safety: {},
    sport: {},
  });

  /**
   * Receive all MVS Codes from the API Server.
   */
  const getBodycodes = (): Promise<void> => new Promise((resolve, reject) => {
    salesApiService.getBodycodes()
      .then((bodycodes) => {
        $scope.bodycodes = bodycodes;
        return resolve();
      })
      .catch((e) => {
        logService.error(e);
        return reject(e);
      });
  });

  /**
   * Get a list of all bodycodes that are included in
   * vehicle stock but not in the mvs table and display them.
   */
  const getMissingBodycodes = (): Promise<void> => new Promise((resolve, reject) => {
    salesApiService.getMissingBodycodes()
      .then((codes) => {
        $scope.missingBodycodes = codes;
        return resolve();
      })
      .catch((e) => {
        logService.error(e);
        return reject(e);
      });
  });

  /**
   * Export the current tables selection as csv.
   */
  $scope.exportDataset = () => {
    if (!$scope.bodycodes) return;

    tableService.exportDataset<Bodycode>(
      $scope.bodycodes,
      'autoboard-bodycodes',
    );
  };

  const updateTables = () => {
    // const self = this;
    $scope.bodycodeTable.settings({
      dataset: $scope.bodycodes,
    });
    interfaceService.applyScope($scope);
  };

  const init = () => {
    $scope.bodycodeTable = new NgTableParams();

    getBodycodes()
      .then(() => getMissingBodycodes())
      .then(() => updateTables());
  };

  const updateBodycode = (bodycode: Bodycode) => {
    salesApiService.putBodycode(bodycode)
      .then(() => {
        $scope.overlay.visibility = false;
        $scope.editBodycode = null;
        toastService.toast(ToastType.SUCCESS, 'Bodycode gespeichert.');
        init();
      })
      .catch((e) => {
        toastService.toast(ToastType.ERROR, 'Fehler beim Speichern des Bodycodes');
        logService.error(e);
      });
  };

  const addBodycode = (bodcode: Bodycode) => {
    salesApiService.postBodycode(bodcode)
      .then(() => {
        $scope.overlay.visibility = false;
        $scope.editBodycode = null;
        toastService.toast(ToastType.SUCCESS, 'Bodycode gespeichert.');
        init();
      })
      .catch((e) => {
        toastService.toast(ToastType.ERROR, 'Fehler beim Anlegen des Bodycodes');
        logService.error(e);
      });
  };

  $scope.submitForm = () => {
    const clonedBodycode = objectService.deepCopy($scope.editBodycode as Bodycode);
    clonedBodycode.source = 'Dealer Overwrite';

    formToolsService.traverseAndTransform(clonedBodycode, [
      { key: 'bodycode', type: 'STRING' },
      { key: 'make', type: 'STRING' },
      { key: 'model', type: 'STRING' },
      { key: 'modelDescription', type: 'STRING' },
      { key: 'hsn', type: 'STRING' },
      { key: 'tsn', type: 'STRING' },
    ]);

    if (!$scope.addNewBodycode) return updateBodycode(clonedBodycode);
    return addBodycode(clonedBodycode);
  };

  /**
   * @param {int} bodycodeId
   */
  $scope.removeBodycode = (bodycodeId: string) => {
    salesApiService.deleteBodycode(bodycodeId)
      .then(() => {
        $scope.overlay.visibility = false;
        $scope.editBodycode = null;
        toastService.toast(ToastType.SUCCESS, 'Bodycode gelöscht.');
        init();
      })
      .catch((e) => logService.error(e));
  };

  /**
   * Toggle the visability of the overlay for editing and adding a new MVS
   * @param {Object} bodycode - null when new bodycode should be added
   */
  $scope.openEditOverlay = (bodycode: Omit<Bodycode, '_id'> | null, newCode: boolean) => {
    $scope.overlay.visibility = true;

    if (!newCode) {
      $scope.editBodycode = bodycode;
      $scope.addNewBodycode = false;
      $scope.overlay.title = 'Bodycode bearbeiten';
    } else {
      $scope.editBodycode = getEmptyBodycode();

      if (bodycode) $scope.editBodycode = bodycode;
      $scope.addNewBodycode = true;
      $scope.overlay.title = 'Bodycode anlegen';
    }
  };

  $scope.addMissingBodycode = (code: string) => {
    $scope.openEditOverlay(getEmptyBodycode(code), true);
  };

  $scope.copyBodycode = (code: Bodycode): void => {
    const newBodycode: Partial<Bodycode> = JSON.parse(JSON.stringify(code));
    newBodycode.bodycode = '';
    delete newBodycode._id;
    $scope.openEditOverlay(newBodycode as Bodycode, true);
  };

  $scope.hideEditOverlay = () => {
    $scope.overlay.visibility = false;
  };

  /**
   * Remove all applied filters from table
   */
  $scope.removeAllFilter = () => {
    $scope.bodycodeTable.filter({});
  };

  /**
   * Remove the supplied filter from the view's table.
   * @param {Object} filter
   */
  $scope.removeFilter = (filter) => {
    delete $scope.bodycodeTable.filter()[filter];
  };

  init();
}
